export type IncidentSeverity = 'low' | 'medium' | 'high' | 'critical';

export type IncidentStatus = 'open' | 'investigating' | 'resolved' | 'closed';

export type IncidentType =
  | 'unauthorized_access'
  | 'data_breach'
  | 'payment_fraud'
  | 'account_takeover'
  | 'brute_force'
  | 'suspicious_login'
  | 'configuration_drift'
  | 'webhook_tampering'
  | 'rate_limit_exceeded'
  | 'privilege_escalation';

export type RestoreStatus =
  | 'pending'
  | 'confirmed'
  | 'in_progress'
  | 'completed'
  | 'failed'
  | 'cancelled';

export type SnapshotType =
  | 'full_schema'
  | 'rls_policies'
  | 'paypal_config'
  | 'user_roles'
  | 'billing_state';

export type AlertType = 'security' | 'billing' | 'system' | 'compliance';

export interface SecurityIncident {
  id: string;
  incident_type: IncidentType;
  severity: IncidentSeverity;
  title: string;
  description: string | null;
  status: IncidentStatus;
  affected_systems: Record<string, unknown> | null;
  affected_user_ids: string[] | null;
  ip_address: string | null;
  geolocation: Record<string, unknown> | null;
  action_history: Record<string, unknown> | null;
  rollback_available: boolean;
  root_cause: string | null;
  timeline_history: Record<string, unknown> | null;
  related_log_ids: string[] | null;
  created_at: string;
  updated_at: string;
  resolved_at: string | null;
  resolved_by: string | null;
}

export interface SecurityAlert {
  id: string;
  alert_type: AlertType;
  severity: IncidentSeverity;
  title: string;
  message: string;
  source_system: string | null;
  related_incident_id: string | null;
  acknowledged_by: string | null;
  acknowledged_at: string | null;
  created_at: string;
  expires_at: string | null;
  metadata: Record<string, unknown> | null;
}

export interface AuditSnapshot {
  id: string;
  snapshot_type: SnapshotType;
  snapshot_data: Record<string, unknown>;
  integrity_hash: string;
  created_by: string;
  created_at: string;
  is_baseline: boolean;
  description: string | null;
  environment: string | null;
}

export interface RestoreEvent {
  id: string;
  restore_type: string;
  snapshot_id: string;
  initiated_by: string;
  reason: string;
  confirmation_token: string | null;
  status: RestoreStatus;
  pre_restore_snapshot_id: string | null;
  post_restore_integrity_check: Record<string, unknown> | null;
  created_at: string;
  completed_at: string | null;
}

export interface PaypalEnvironmentSnapshot {
  id: string;
  environment: string;
  webhook_config: Record<string, unknown> | null;
  subscription_plans: Record<string, unknown> | null;
  pricing_config: Record<string, unknown> | null;
  api_credentials_hash: string | null;
  checkout_script_hash: string | null;
  captured_at: string;
  captured_by: string | null;
  drift_detected: boolean;
  drift_details: Record<string, unknown> | null;
}

export const SEVERITY_COLORS: Record<IncidentSeverity, string> = {
  critical: 'bg-red-100 text-red-800',
  high: 'bg-orange-100 text-orange-800',
  medium: 'bg-yellow-100 text-yellow-800',
  low: 'bg-blue-100 text-blue-800',
};

export const SEVERITY_DOT: Record<IncidentSeverity, string> = {
  critical: 'bg-red-500',
  high: 'bg-orange-500',
  medium: 'bg-yellow-500',
  low: 'bg-blue-500',
};

export const SEVERITY_LABEL: Record<IncidentSeverity, string> = {
  critical: 'Critical',
  high: 'High',
  medium: 'Medium',
  low: 'Low',
};

export const STATUS_COLORS: Record<IncidentStatus, string> = {
  open: 'bg-red-100 text-red-800',
  investigating: 'bg-orange-100 text-orange-800',
  resolved: 'bg-green-100 text-green-800',
  closed: 'bg-gray-100 text-gray-800',
};

export const STATUS_LABEL: Record<IncidentStatus, string> = {
  open: 'Open',
  investigating: 'Investigating',
  resolved: 'Resolved',
  closed: 'Closed',
};

export const INCIDENT_TYPE_LABEL: Record<IncidentType, string> = {
  unauthorized_access: 'Unauthorized Access',
  data_breach: 'Data Breach',
  payment_fraud: 'Payment Fraud',
  account_takeover: 'Account Takeover',
  brute_force: 'Brute Force',
  suspicious_login: 'Suspicious Login',
  configuration_drift: 'Configuration Drift',
  webhook_tampering: 'Webhook Tampering',
  rate_limit_exceeded: 'Rate Limit Exceeded',
  privilege_escalation: 'Privilege Escalation',
};
